import { useEffect, useState } from "react";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Upload } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Modal } from "@/shared/ui/Modal";
import { Textarea } from "@/shared/ui/textarea";
import { useSessionStore } from "@/entities/session";
import {
  useCreateMentorProfile,
  useEditMentorProfile,
  useMentorProfile,
} from "@/entities/mentors-profile";
import type { MentorProfileForm } from "./model/schema";
import { mentorProfileSchema } from "./model/schema";

export const MentorProfileModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useSessionStore();
  const username = user?.username!;
  const userId = user?._id!;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<MentorProfileForm>({
    resolver: zodResolver(mentorProfileSchema),
  });

  const { mutateAsync: createProfile, isPending: isCreationPending } =
    useCreateMentorProfile();
  const { mutateAsync: editProfile, isPending: isEditPending } =
    useEditMentorProfile();
  const { data: mentorProfile, isSuccess: hasProfile } = useMentorProfile({
    userId,
  });

  const convertToJpeg = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > 512 || img.height > 320) {
          return reject(
            new Error("Разрешение не должно превышать 512×320 пикселей"),
          );
        }
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Ошибка конвертации изображения"));
          },
          "image/jpeg",
          0.9,
        );
      };
      img.onerror = () => reject(new Error("Ошибка загрузки изображения"));
      img.src = URL.createObjectURL(file);
    });
  };

  const onSubmit = async (data: MentorProfileForm) => {
    try {
      const formData = new FormData();
      const file = data.image;

      if (file) {
        try {
          const imageBlob = await convertToJpeg(file);
          formData.append("image", imageBlob, `${username}.jpeg`);
        } catch (error) {
          console.error(error);
          return;
        }
      }

      formData.append("description", data.description);

      if (hasProfile && mentorProfile.data?.id) {
        formData.append("id", String(mentorProfile.data.id));
        await editProfile(formData);
      } else {
        await createProfile(formData as any);
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error("Ошибка при подготовке данных:", err);
    }
  };

  useEffect(() => {
    if (mentorProfile?.data?.description) {
      setValue("description", mentorProfile.data.description);
    }
  }, [mentorProfile, setValue]);

  return (
    <div className="flex flex-col">
      <Label className="text-sm font-normal md:text-[12px]">Анкета</Label>
      <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Modal.Trigger>
          {hasProfile ? "Отредактировать анкету" : "Заполните свою анкету"}
        </Modal.Trigger>
        <Modal.Content className="md:min-w-[650px] [&>*]:font-medium">
          <Modal.Title>Наставник #{username}</Modal.Title>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <p className="text-black/60 dark:text-white/60">
                Сопроводительный текст
              </p>
              <Textarea
                placeholder="Описание"
                className="h-20"
                {...register("description")}
              />
              {errors.description && (
                <p className="text-sm text-red-500">
                  {errors.description.message}
                </p>
              )}
            </div>

            <Controller
              control={control}
              name="image"
              render={({ field: { onChange, value } }) => (
                <div className="flex items-center gap-2 rounded-lg bg-gray-100 p-3 dark:bg-gray-800">
                  <label className="flex w-full cursor-pointer items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Upload className="min-w-6 text-[#2990FF]" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {value?.name ||
                          "Изображение для анкеты. (Разрешение изображения не должно превышать 512×320px, изображение автоматически конвертируется в JPEG)"}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg, image/png"
                      className="hidden"
                      onChange={(e) => onChange(e.target.files?.[0])}
                    />
                    <span className="text-sm text-[#2990FF]">Загрузить</span>
                  </label>
                </div>
              )}
            />
            {errors.image && (
              <p className="text-sm text-red-500">
                {errors.image.message as string}
              </p>
            )}

            <div className="mt-4 space-y-2">
              <Button
                type="submit"
                className="w-full"
                disabled={isCreationPending || isEditPending}
              >
                {isCreationPending || isEditPending
                  ? "Отправка..."
                  : "Отправить анкету"}
              </Button>

              <Modal.Close />
            </div>
          </form>
        </Modal.Content>
      </Modal>
    </div>
  );
};
